"use client";
import { usePricing } from "@/components/pricing/PricingProvider";

import { useEffect, useRef } from "react";
import { sendGTMEvent } from "@next/third-parties/google";
import { trackAdsVehicleView } from "@/lib/analytics/googleAds";
import { createViewItemEventData, type VehicleData, type DealerData, type AnalyticsContext } from "@/utils/ga4-events";

import type { VehicleDetails, DealerInfo } from "@/types/vehicle-details";

interface VDPAnalyticsProps {
  vehicleData: VehicleDetails;
  dealerData: DealerInfo | null;
}

export default function VDPAnalytics({ vehicleData, dealerData }: VDPAnalyticsProps) {
    const { getNoHagglePrice } = usePricing();
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return;
    firedRef.current = true;

    const vehicleName = `${vehicleData.year || ""} ${vehicleData.make || ""} ${vehicleData.model || ""} ${vehicleData.trim || ""}`.trim();

    const vData: VehicleData = {
      vin: vehicleData.vin,
      stockId: vehicleData.stockId,
      name: vehicleName,
      price: getNoHagglePrice(vehicleData.price) ?? vehicleData.price,
      make: vehicleData.make,
      model: vehicleData.model,
      year: vehicleData.year
    };

    const dData: DealerData | undefined = dealerData && (dealerData.dealerName || dealerData.state)
      ? {
          ...(dealerData.dealerName ? { name: dealerData.dealerName } : {}),
          ...(dealerData.state ? { state: dealerData.state } : {}),
        }
      : undefined;

    const context: AnalyticsContext = {
      buttonLocation: "hero"
    };

    try {
      sendGTMEvent({ event: "view_item", ...createViewItemEventData("vehicle", vData, context, dData) });
      trackAdsVehicleView({ id: vData.stockId?.trim() || vData.vin, ...(typeof vData.price === "number" ? { price: vData.price } : {}) });
    } catch (e) {
      // Avoid breaking UI on analytics failure
      console.warn("VDPAnalytics: failed to send view_item", e);
    }
  }, [vehicleData, dealerData, getNoHagglePrice]);

  return null;
}
